import { useEffect, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { Alert, Button, Stack, Typography } from '@mui/material';
import useAuth from 'src/hooks/useAuth';
import useSafeTranslation from 'src/hooks/useSafeTranslation';
import {
  PUSH_CONFIG,
  PUSH_DEVICES,
  REMOVE_PUSH_DEVICE
} from 'src/graphQL/notifications';
import {
  currentPushSubscription,
  disconnectPush,
  enablePush,
  pushOwner,
  pushSupport
} from 'src/utils/notificationPush';

export default function PushSettings() {
  const { t } = useSafeTranslation();
  const { user } = useAuth();
  const client = useApolloClient();
  const { data, error: configError } = useQuery(PUSH_CONFIG, {
    fetchPolicy: 'network-only'
  });
  const [endpoint, setEndpoint] = useState(null);
  const [support, setSupport] = useState(pushSupport);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const {
    data: deviceData,
    error: deviceError,
    refetch
  } = useQuery(PUSH_DEVICES, {
    variables: { currentEndpoint: endpoint },
    fetchPolicy: 'network-only'
  });
  const devices = deviceData?.myPushDevices || [];
  const active =
    devices.some((device) => device.current) && pushOwner() === String(user.id);

  useEffect(() => {
    let alive = true;
    currentPushSubscription()
      .then((sub) => {
        if (alive) setEndpoint(sub?.endpoint || null);
      })
      .catch(() => {
        if (alive) setError(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  const enable = async () => {
    setBusy(true);
    setError(null);
    try {
      const sub = await enablePush(
        client,
        user.id,
        data.universePushConfig.publicKey
      );
      setEndpoint(sub.endpoint);
      await refetch({ currentEndpoint: sub.endpoint });
    } catch (caught) {
      setError(caught);
    } finally {
      setSupport(pushSupport());
      setBusy(false);
    }
  };
  const disable = async (device) => {
    setBusy(true);
    setError(null);
    try {
      if (device.current) {
        await disconnectPush(client);
        setEndpoint(null);
      } else
        await client.mutate({
          mutation: REMOVE_PUSH_DEVICE,
          variables: { id: device.id }
        });
      await refetch();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };
  const status = {
    install: t('notifications.push.install', {
      defaultValue:
        'Su iPhone o iPad, apri Condividi → Aggiungi alla schermata Home. Avvia Universe dalla Home per attivare le notifiche.'
    }),
    unsupported: t('notifications.push.unsupported', {
      defaultValue:
        'Questo dispositivo non supporta le notifiche push. Le notifiche restano disponibili qui.'
    }),
    denied: t('notifications.push.denied', {
      defaultValue:
        'Permesso negato. Consenti le notifiche nelle impostazioni del sito del browser o nelle impostazioni Notifiche del dispositivo, poi riapri Universe.'
    }),
    supported: active
      ? t('notifications.push.active', {
          defaultValue: 'Push attive su questo dispositivo.'
        })
      : t('notifications.push.off', {
          defaultValue: 'Push disattivate su questo dispositivo.'
        })
  }[support];

  return (
    <Stack spacing={1.5} sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
      <Typography variant="subtitle2">
        {t('notifications.push.title', {
          defaultValue: 'Notifiche sui dispositivi'
        })}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {status}
      </Typography>
      {(error || configError || deviceError) && (
        <Alert severity="error">
          {t('notifications.push.error', {
            defaultValue:
              'Impossibile aggiornare le push. Verifica la connessione e riprova.'
          })}
        </Alert>
      )}
      {data && !data.universePushConfig.enabled && (
        <Typography variant="body2" color="text.secondary">
          {t('notifications.push.unconfigured', {
            defaultValue:
              'Le push non sono ancora disponibili. La campanella continua a funzionare.'
          })}
        </Typography>
      )}
      {support === 'supported' && !active && data?.universePushConfig.enabled && (
        <Button variant="outlined" onClick={enable} disabled={busy}>
          {t('notifications.push.enable', {
            defaultValue: 'Attiva notifiche push'
          })}
        </Button>
      )}
      {devices.map((device) => (
        <Stack
          key={device.id}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Typography variant="body2">
            {device.label}
            {device.current
              ? ` (${t('notifications.push.thisDevice', {
                  defaultValue: 'questo dispositivo'
                })})`
              : ''}
          </Typography>
          <Button
            size="small"
            disabled={busy}
            onClick={() => disable(device)}
            aria-label={t('notifications.push.disableLabel', {
              defaultValue: 'Disattiva push su {{device}}',
              device: device.label
            })}
          >
            {t('notifications.push.disable', { defaultValue: 'Disattiva' })}
          </Button>
        </Stack>
      ))}
    </Stack>
  );
}
